import React, { useState, useEffect } from "react";
import { ArrowDownIcon } from "../assets/icons";
import { getGunlukIsTakip, getGunlukIsTakipExport } from "../services/personelService";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const GunlukIsTakip = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Format date from "2025-11-05T15:43:05.073" to "05.11.2025 15:43:05"
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getGunlukIsTakip();
      // The response can be an array or a single object, extract rows from each item
      let allRows = [];
      if (Array.isArray(response)) {
        // Check if first item has a 'rows' property (array of objects with rows)
        if (response.length > 0 && response[0].rows && Array.isArray(response[0].rows)) {
          response.forEach(item => {
            if (item.rows && Array.isArray(item.rows)) {
              allRows = [...allRows, ...item.rows];
            }
          });
        } else {
          // If response is directly an array of row objects
          allRows = response;
        }
      } else if (response && response.rows && Array.isArray(response.rows)) {
        // If it's a single object with rows
        allRows = response.rows;
      }
      setData(allRows);
      setFilteredData(allRows);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      setError(error.message || 'Veri yükleme hatası oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter data based on search term (PersonelAdSoyAd)
    if (!searchTerm.trim()) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) =>
        item.PersonelAdSoyAd?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const blob = await getGunlukIsTakipExport();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const today = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute('download', `gunluk-is-takip-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel indirme hatası:", err);
      setError(err.message || 'Excel indirilemedi');
    } finally {
      setIsExporting(false);
    }
  };

  if (error && !data.length) {
    return (
      <div className="people-container gunluk-is-takip-container">
        <h1 className="page-title">Günlük İş Takip</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="people-container gunluk-is-takip-container">
      <h1 className="page-title">Günlük İş Takip</h1>

      <div className="search-container" style={{ marginBottom: '20px' }}>
        <div className="search-inputs">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Personel adı soyadı ara..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <div className="search-input-group">
            <button
              onClick={handleExportExcel}
              className="download-excel-button"
              disabled={isExporting}
            >
              {isExporting ? "İndiriliyor..." : "Excel Çıktısı Al"}
              <ArrowDownIcon color='white' className="filter-icon" />
            </button>
          </div>
        </div>
      </div>

      <DataTable
        className="summary-table gunluk-is-takip-table"
        loading={loading}
        value={filteredData}
        paginator
        rows={25}
        stripedRows
        sortMode="single"
        emptyMessage="Veri bulunamadı"
      >
        <Column
          field="PersonelAdSoyAd"
          header="Personel Ad Soyad"
          sortable
        />
        <Column
          field="OncekiAksiyonu"
          header="Önceki Aksiyonu"
        />
        <Column
          field="OncekiAksiyonTarihi"
          header="Önceki Aksiyon Tarihi"
          sortable
          body={(rowData) => formatDateTime(rowData.OncekiAksiyonTarihi)}
          sortField="OncekiAksiyonTarihi"
        />
        <Column
          field="SonrakiAksiyonu"
          header="Sonraki Aksiyonu"
        />
        <Column
          field="SonrakiAksiyonTarihi"
          header="Sonraki Aksiyon Tarihi"
          sortable
          headerStyle={{ justifyContent: 'center' }}
          body={(rowData) => formatDateTime(rowData.SonrakiAksiyonTarihi)}
          sortField="SonrakiAksiyonTarihi"
        />
        <Column
          field="GecenSure"
          header="Geçen Süre"
          sortable
          body={(rowData) => {
            const gecenSure = rowData.GecenSure || 0;
            let className = 'gecen-sure-cell';
            
            if (gecenSure < 30) {
              className += ' gecen-sure-low';
            } else if (gecenSure >= 30 && gecenSure < 60) {
              className += ' gecen-sure-medium';
            } else if (gecenSure >= 60) {
              className += ' gecen-sure-high';
            }
            
            return <span className={className}>{gecenSure} dk</span>;
          }}
          sortField="GecenSure"
        />
      </DataTable>
    </div>
  );
};

export default GunlukIsTakip;

